import './index.css'

import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import api from '@disco/api/server'
import { fetchTrackMetadata } from '@disco/state-management/actions/ServerActions'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { getUser } from '@disco/state-management/selectors/user'

import type { BusinessData } from 'types/business'
import type { Track } from 'types/track'
import type { User } from 'types/user'

import { getShortlistBaseRoute } from 'utils/routing'

import Row from 'components/common/row'
import { Text } from 'components/ds'
import DocumentHeadTags from 'components/document-head-tags'
import { Tab, Tabs } from 'components/ui'
import Alert from 'components/ui/alert'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import ModalWindow from 'components/ui/modal-window'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { getTrackPrivateMetadata } from '../../../../state/selectors/private-metadata'

// The API returns metadata as a plain object, so display order is whatever
// order the server happens to insert keys in — Lyrics has always shown
// first regardless, so this pins that instead of drifting with server
// changes. Unlisted keys keep their original relative order at the end.
const PUBLIC_METADATA_ORDER = [
  'Lyrics',
  'Title',
  'Artist',
  'Album',
  'Composer',
  'Copyright',
  'Grouping',
  'Genre',
  'Year',
  'Release Date',
  'BPM',
  'ISRC',
  'TrackNumber',
  'Tracks',
  'External Id',
  'ProNumber',
  'Comment',
]

const sortByFieldOrder = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const aIndex = PUBLIC_METADATA_ORDER.indexOf(a.name)
    const bIndex = PUBLIC_METADATA_ORDER.indexOf(b.name)
    if (aIndex === -1 && bIndex === -1) return 0
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })

const getMetadataMessage = ({
  attach_writers_to_metadata: attachWriters,
  write_native_tags_to_comments_metadata_enabled: writeNativeTags,
}: BusinessData): React.ReactNode => {
  let message = 'Notes and custom fields don’t write to tracks. '

  if (!attachWriters && !writeNativeTags) {
    message += 'Writers and Tags are set to not write to the metadata. '
  }

  if (!attachWriters && writeNativeTags) {
    message += 'Writers are set to not write to the metadata. '
  }

  if (attachWriters && !writeNativeTags) {
    message += 'Tags are set to not write to the metadata. '
  }

  return (
    <>
      {message}
      This can be changed in{' '}
      <InlineLink to="/settings/business">business settings</InlineLink>.
    </>
  )
}

const PreviewMetadata = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams<{ trackId: string }>()

  const trackId: number = parseInt(params.trackId || '', 10)
  const location = useSelector(getLocationBeforeTransitions)
  const track: Track = useSelector(state =>
    getTrack(state, {
      id: trackId,
    }),
  )
  const user: User = useSelector(getUser)
  const privateMetadata: {
    name: string
    value: React.ReactNode
  }[] = useSelector(state =>
    getTrackPrivateMetadata(state, {
      id: trackId,
    }),
  )
  const [publicMetadata, setPublicMetadata] = useState<
    {
      name: string
      value: React.ReactNode
    }[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<number>(0)

  const fetchDownloadableTrackMetadata = async (trackId: number) => {
    setIsLoading(true)

    try {
      const response = await api.getDownloadableTrackMetadata(trackId)
      const metadata = Object.keys(response)
        .map(key =>
          response[key]
            ? {
                name: key,
                value: response[key],
              }
            : null,
        )
        .filter(Boolean)
      setPublicMetadata(sortByFieldOrder(metadata))
    } catch (error) {}

    setIsLoading(false)
  }

  useEffect(() => {
    dispatch(fetchTrackMetadata(trackId))
    fetchDownloadableTrackMetadata(trackId)
  }, [])
  const handleClose = useCallback(() => {
    const baseRoute = getShortlistBaseRoute(location)
    navigate(baseRoute || '/')
  }, [navigate, location])

  const renderMetadataItem = (item: {
    name: string
    value: React.ReactNode
  }) => (
    <div className="PreviewMetadata-item" key={item.name}>
      <Text as="div" size="1" weight="medium">
        {item.name}
      </Text>
      <Text
        as="div"
        size="1"
        color="gray"
        className="PreviewMetadata-item-value"
      >
        {item.value}
      </Text>
    </div>
  )

  return (
    <ModalWindow
      header={<Heading size={700}>Preview track information</Heading>}
      isOpen={true}
      width="small"
      onClose={handleClose}
    >
      <DocumentHeadTags title="Preview track information | DISCO" />
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <Alert
            actions={
              <InlineLink
                to="https://support.disco.ac/home/updating-track-metadata"
                rel="noopener noreferrer"
                target="_blank"
              >
                Find out more
              </InlineLink>
            }
            status="change"
          >
            What track information will write to the downloaded tracks?
          </Alert>
        </div>
      </Row>
      <Tabs
        className="Tabs--modal"
        index={selectedTab}
        onChange={setSelectedTab}
      >
        <Tab label="Will write">
          <div className="PreviewMetadata-info">
            {isLoading ? 'Loading...' : publicMetadata.map(renderMetadataItem)}
          </div>
        </Tab>
        <Tab label="Won’t write">
          <div className="PreviewMetadata-message">
            <div className="PreviewMetadata-message-icon">
              <Icon type="info-outline" width={16} height={16} />
            </div>
            <div className="PreviewMetadata-message-content">
              {getMetadataMessage(user.business)}
            </div>
          </div>
          <div className="PreviewMetadata-info">
            {track ? privateMetadata.map(renderMetadataItem) : 'Loading...'}
          </div>
        </Tab>
      </Tabs>
    </ModalWindow>
  )
}

export default PreviewMetadata
